import { Select } from 'antd';

import {
  ConditionOperator,
  PropertyConditionName,
  WorkflowConfigurationMap,
} from '@invenco/common-domain/enums';

import { DeleteButton } from '@/components-ant/button';
import { Form } from '@/components-ant/form';
import { conditionOperatorTitle, propertyConditionNameTitle } from '@/shared/title-maps';

import { useWorkflowScopeType } from '../../WorkflowScopeTypeContext';
import { getPropertyConditionDetails } from '../../helpers/conditions';
import {
  conditionOperatorKey,
  conditionPropertyKey,
  conditionValueKey,
  FormKey,
  getFormItemRules,
} from '../../helpers/forms';
import { ValueFormItem } from '../value-form-item/ValueFormItem';
import { ConditionInputRow } from './styles';

type Props = {
  editable: boolean;
  formKey: FormKey;
  onDelete: () => void;
  onChangeType: () => void;
};

export function PropertyConditionInput({ formKey, onDelete, onChangeType, editable }: Props) {
  const scopeType = useWorkflowScopeType();
  return (
    <ConditionInputRow data-testid="condition-row">
      <Form.Item
        label="Condition property"
        name={[...formKey, conditionPropertyKey]}
        rules={[{ required: true, message: 'Please select a property' }]}
      >
        <Select
          disabled={!editable}
          onSelect={onChangeType}
          options={Object.keys(
            WorkflowConfigurationMap[scopeType].propertyConditionEvaluationAttributes,
          ).map((property) => ({
            value: property,
            label: propertyConditionNameTitle[property as PropertyConditionName],
          }))}
          showSearch
          filterOption={(input, option) =>
            option?.label.toLocaleLowerCase().includes(input.toLocaleLowerCase()) ?? false
          }
        />
      </Form.Item>
      <Form.Item noStyle dependencies={[[...formKey, conditionPropertyKey]]}>
        {({ getFieldValue, setFieldValue }) => {
          const property = getFieldValue([
            ...formKey,
            conditionPropertyKey,
          ]) as PropertyConditionName;
          const { operators } = getPropertyConditionDetails(property);
          return (
            <Form.Item
              label="Condition operator"
              name={[...formKey, conditionOperatorKey]}
              rules={[{ required: true, message: 'Please select an operator' }]}
            >
              <Select
                disabled={!editable}
                options={operators.map((operator) => ({
                  value: operator,
                  label: conditionOperatorTitle[operator],
                }))}
                onSelect={(value) => {
                  // reset to blank value when operator is changed to a non-value operator
                  if (
                    value === ConditionOperator.IS_EMPTY ||
                    value === ConditionOperator.IS_NOT_EMPTY
                  ) {
                    // clear value if switching to a non-value operator
                    setFieldValue([...formKey, conditionValueKey], '');
                  }
                }}
                showSearch
                filterOption={(input, option) =>
                  option?.label.toLocaleLowerCase().includes(input.toLocaleLowerCase()) ?? false
                }
              />
            </Form.Item>
          );
        }}
      </Form.Item>
      <Form.Item
        noStyle
        dependencies={[
          [...formKey, conditionPropertyKey],
          [...formKey, conditionOperatorKey],
        ]}
      >
        {({ getFieldValue }) => {
          const operator = getFieldValue([...formKey, conditionOperatorKey]) as ConditionOperator;
          const isNonValueOperator =
            operator === ConditionOperator.IS_EMPTY || operator === ConditionOperator.IS_NOT_EMPTY;

          const property = getFieldValue([
            ...formKey,
            conditionPropertyKey,
          ]) as PropertyConditionName;
          const {
            operators,
            required = true,
            ...itemDetails
          } = getPropertyConditionDetails(property);
          return (
            <ValueFormItem
              disabled={!editable || isNonValueOperator}
              label="Condition value"
              name={[...formKey, conditionValueKey]}
              rules={getFormItemRules({
                type: itemDetails.type,
                required: required && !isNonValueOperator,
              })}
              {...itemDetails}
            />
          );
        }}
      </Form.Item>
      <DeleteButton
        htmlType="button"
        aria-label="Remove condition"
        onClick={onDelete}
        disabled={!editable}
      />
    </ConditionInputRow>
  );
}
